import clsx from 'clsx';
import {
  Inbox,
  MessagesSquare,
  Bell,
  Archive,
  LogOut,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { observeAuth, signOutUser } from '@/lib/auth';

export type LeftRailTab = 'inbox' | 'threads' | 'notifications' | 'archive';

interface Props {
  activeTab: LeftRailTab;
  onTabChange: (tab: LeftRailTab) => void;
  mobileCollapsed?: boolean;
}

// The leftmost column — icon-only nav, dark surface, ~64px wide. Anchors the
// UI so the mid/right panes float against a consistent frame.
export function LeftRail({ activeTab, onTabChange }: Props) {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => observeAuth(setUser), []);

  const items: { id: LeftRailTab; label: string; Icon: typeof Inbox }[] = [
    { id: 'inbox', label: 'Inbox', Icon: Inbox },
    { id: 'threads', label: 'Threads', Icon: MessagesSquare },
    { id: 'notifications', label: 'Notifications', Icon: Bell },
    { id: 'archive', label: 'Archive', Icon: Archive },
  ];

  return (
    <nav
      aria-label="Primary"
      className="flex h-full w-16 flex-col items-center justify-between bg-rail-900 py-4 text-rail-200 shadow-rail"
    >
      <div className="flex flex-col items-center gap-6">
        <Logo />
        <div className="flex flex-col items-center gap-2">
          {items.map(({ id, label, Icon }) => (
            <IconButton
              key={id}
              label={label}
              active={activeTab === id}
              onClick={() => onTabChange(id)}
            >
              <Icon size={18} />
            </IconButton>
          ))}
        </div>
      </div>

      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          title="Sign out"
          aria-label="Sign out"
          onClick={() => {
            void signOutUser();
          }}
          className="flex h-9 w-9 items-center justify-center rounded-xl text-rail-400 transition hover:bg-rail-700 hover:text-rail-200"
        >
          <LogOut size={16} />
        </button>
        <Avatar email={user?.email ?? undefined} />
      </div>
    </nav>
  );
}

function Logo() {
  return (
    <div
      aria-hidden
      className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500 to-accent-700 text-[11px] font-bold tracking-tight text-white shadow-glow"
    >
      rta
    </div>
  );
}

function IconButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      className={clsx(
        'relative flex h-10 w-10 items-center justify-center rounded-xl transition',
        active
          ? 'bg-rail-700 text-white'
          : 'text-rail-400 hover:bg-rail-800 hover:text-rail-200',
      )}
    >
      {active && (
        <span
          aria-hidden
          className="absolute -left-3 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-accent-500"
        />
      )}
      {children}
    </button>
  );
}

function Avatar({ email }: { email?: string }) {
  const initial = (email?.[0] ?? 'F').toUpperCase();
  return (
    <div
      title={email ?? 'Founder'}
      aria-label={`Signed in as ${email ?? 'founder'}`}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-rail-600 bg-rail-800 text-xs font-semibold text-rail-200"
    >
      {initial}
    </div>
  );
}
